/* PŘEKLAD MIMO REACT — pro čisté moduly, které dostávají `lang`, ne `t()`.
 * ============================================================================
 *
 * PROČ TENHLE SOUBOR VZNIKL
 * -------------------------
 * `t()` z `contexts/LanguageContext.js` je hook — žije uvnitř Reactu. Jenže
 * věty, které poradce vysloví nahlas, se schválně skládají v JÁDŘE
 * (`lib/portfolio/*`), aby žádná obrazovka nemohla ukázat číslo a zapomenout
 * přiznat pokrytí. Jádro tedy potřebuje slovník, ale hook použít nemůže.
 *
 * Do 17. 8. 2026 to jádro řešilo ternárem `lang === "sk" ? … : …`. Ten zná
 * jen dva jazyky, takže Vietnamec i Ukrajinec spadli do české větve — přesně
 * to, na co si majitel stěžoval. Tenhle modul je ta chybějící část: čistá
 * funkce nad TÝMIŽ pěti slovníky, které registruje `App.js`.
 *
 * Slovníky se importují přímo (ne přes `registerTranslations`) schválně:
 * jádro se testuje bez Reactu a bez `App.js`, takže registr by byl prázdný
 * a `vazenaNakladovost(…, { lang: "sk" })` by tiše vrátila češtinu.
 *
 * CO TENHLE MODUL UMÍ NAVÍC PROTI `t()`
 * -------------------------------------
 *   1) PARAMETRY — `{n}` v hodnotě se nahradí. Bez toho se věta láme na kusy
 *      a skládá v JSX, což v ukrajinštině a vietnamštině rozbije slovosled.
 *   2) PLURÁLY přes `Intl.PluralRules`. České pravidlo 1 / 2–4 / 5+ sedí na
 *      cs, sk, vi i en beze zbytku, ale na ukrajinštině dá špatný tvar u 32
 *      čísel ze 101 (21–24, 31–34, …) — „21 клієнтів" místo „21 клієнт".
 *
 * NESAHAT: měna. Ta jde ze ZEMĚ TENANTA (`lib/tenantMena.js`), nikdy z jazyka
 * — viz `docs/MENA-A-JAZYK.md`.
 */

import cs from "@/data/translations/cs";
import sk from "@/data/translations/sk";
import en from "@/data/translations/en";
import uk from "@/data/translations/uk";
import vi from "@/data/translations/vi";

import { dosad, kategoriePluralu, priponaPluralu } from "./pravidla";

const SLOVNIKY = { cs, sk, en, uk, vi };

/* Tabulky plurálů a dosazování `{parametru}` si tenhle modul UŽ NEDRŽÍ SÁM —
 * bydlí v `./pravidla.js`, protože přesně totéž potřebuje i hook `t()`
 * v `contexts/LanguageContext.js`. Dvě kopie téhle tabulky by se dřív nebo
 * později rozešly a Ukrajinec by dostal jiný tvar v grafu než v popisku
 * pod ním. `dosad` se re-exportuje, ať se nemusí přepisovat volající. */
export { dosad, kategoriePluralu };

/** Bezpečné čtení `a.b.c` ze zanořeného objektu. */
function projdi(objekt, klic) {
  let uzel = objekt;
  for (const kus of String(klic).split(".")) {
    if (uzel == null) return undefined;
    uzel = uzel[kus];
  }
  return typeof uzel === "string" ? uzel : undefined;
}

/**
 * Text z pětijazyčného slovníku.
 *
 * Pořadí: zvolený jazyk → čeština → fallback → klíč. Stejné jako `t()`
 * v LanguageContextu, ať se obrazovka a jádro nechovají každý jinak.
 *
 * @param {string} lang kód jazyka (cs/sk/en/uk/vi)
 * @param {string} klic tečková cesta do slovníku
 * @param {string} [fallback] český text, kdyby klíč chyběl
 * @param {object} [params] hodnoty pro `{zastupne}` symboly
 */
export function textJazyka(lang, klic, fallback, params) {
  const slovnik = SLOVNIKY[lang] || SLOVNIKY.cs;
  const nalez = projdi(slovnik, klic) ?? projdi(SLOVNIKY.cs, klic) ?? fallback ?? klic;
  return dosad(nalez, params);
}

/**
 * Správný tvar podle počtu. Slovník musí mít trojici
 * `<zaklad>One` / `<zaklad>Few` / `<zaklad>Many`.
 *
 * `{n}` je k dispozici automaticky (stejně jako u `t.plural` v Reactu),
 * ať trojice může znít i „{n} kroků" a nemusí se skládat v JSX.
 *
 * @example pluralJazyka("uk", "nastroje.pdVety.polozka", 21) → „позиція"
 */
export function pluralJazyka(lang, zaklad, n, params) {
  return textJazyka(lang, `${zaklad}${priponaPluralu(lang, n)}`, undefined, { n, ...(params || null) });
}

/**
 * Překladač navázaný na jeden jazyk — ať se `lang` nemusí protahovat každým
 * voláním. Vrací funkci se stejným podpisem jako `textJazyka` bez prvního
 * argumentu.
 */
export function prekladac(lang) {
  const t = (klic, fallback, params) => textJazyka(lang, klic, fallback, params);
  t.plural = (zaklad, n, params) => pluralJazyka(lang, zaklad, n, params);
  t.lang = lang;
  return t;
}
